// The usage meter in the sidebar's foot (brand v2, owner 2026-11-04: quiet until it matters; the
// plan and when it changes, the count, a thin bar, the percent from the first usage alert) (D47, D107; plan step 5.1; Console D's nav-meter): on the
// trial (14 days of Pro), the days left and its 50k captured sessions; on a plan, the period's sessions against
// its cap or included sessions. The count comes from GET /orgs/{orgId}/usage; until it answers
// (or if it cannot), the trial meter tracks time and states the cap. It links to the plan.
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import type { OrgMembership, Usage } from '../api/types'
import { useI18n } from '../i18n'

export const PLAN_KEY = {
  free: 'plan.free',
  trial: 'plan.trial',
  pro: 'plan.pro',
  enterprise: 'plan.enterprise',
} as const

export const TRIAL_DAYS = 14
export const TRIAL_SESSIONS = 50_000
const DAY = 86_400_000

export function trialState(
  org: Pick<OrgMembership, 'plan' | 'status' | 'trial_ends_at'>,
  now = Date.now(),
) {
  if (org.plan !== 'trial' || !org.trial_ends_at) return undefined
  const left = Math.max(0, Math.ceil((new Date(org.trial_ends_at).getTime() - now) / DAY))
  // Ended, until the next metering run moves the organization to Free (D107).
  const ended = left === 0
  return { left, ended, used: Math.min(1, (TRIAL_DAYS - left) / TRIAL_DAYS) }
}

/** The sessions meter: how much of the cap (or the included sessions) this period used. */
export function sessionsState(usage: Usage | undefined) {
  if (!usage) return undefined
  const limit =
    usage.cap?.sessions ?? (usage.included_sessions > 0 ? usage.included_sessions : null)
  return {
    used: usage.captured_sessions,
    limit,
    share: limit ? Math.min(1, usage.captured_sessions / limit) : null,
    capped: usage.capped,
  }
}

export function TrialMeter({
  org,
  usage,
  projectId,
}: {
  org: OrgMembership
  usage?: Usage | undefined
  /** The project the plan settings open in; without one the meter is not a link. */
  projectId?: string
}) {
  const { t, number, date } = useI18n()
  const trial = trialState(org)
  const sessions = sessionsState(usage)
  const compact = (n: number) => number(n, { notation: 'compact' })
  // The meter's short count reads like the mock's ("14.2k / 25k"); the spoken one stays as is.
  const short = (n: number) => compact(n).toLowerCase()
  // The count, short to fit beside the days; its words are for screen readers.
  const count: ReactNode = sessions ? (
    <>
      <span className="pm-v" aria-hidden="true">
        <b>{short(sessions.used)}</b>
        {sessions.limit ? <span>{` / ${short(sessions.limit)}`}</span> : null}
        <span className="pm-u">{` ${t('plan.sessionsWord')}`}</span>
      </span>
      <span className="vh">
        {sessions.limit
          ? t('usage.of', { used: compact(sessions.used), limit: compact(sessions.limit) })
          : t('usage.thisPeriod', { used: compact(sessions.used) })}
      </span>
    </>
  ) : trial ? (
    <>
      <span className="pm-v" aria-hidden="true">
        <b>{t('plan.upTo', { count: short(TRIAL_SESSIONS) })}</b>
        <span className="pm-u">{` ${t('plan.sessionsWord')}`}</span>
      </span>
      <span className="vh">{t('trial.cap', { sessions: compact(TRIAL_SESSIONS) })}</span>
    </>
  ) : null
  const share = sessions?.share ?? (sessions ? null : (trial?.used ?? null))
  const ratio = sessions?.limit && sessions.limit > 0 ? sessions.used / sessions.limit : undefined
  // Quiet until it matters (owner, 2026-11-04): the percent shows from the first usage alert
  // (50 %), Pro past its included sessions says how many over, and the bar warms from 80 %.
  const over =
    sessions && !usage?.cap && sessions.limit && sessions.used > sessions.limit
      ? sessions.used - sessions.limit
      : 0
  const aside = over
    ? t('plan.over', { count: short(over) })
    : ratio !== undefined && ratio >= 0.5
      ? number(ratio, { style: 'percent', maximumFractionDigits: 0 })
      : null
  const level = sessions?.capped
    ? 'full'
    : over || (share !== null && share >= 0.8)
      ? 'high'
      : undefined
  // When it changes: the trial's days, else the period's end (Free resets, a paid plan renews).
  const end = usage?.period?.end
  const when = trial
    ? trial.ended
      ? t('trial.ended')
      : t('trial.daysLeft', { days: trial.left })
    : end
      ? t(org.plan === 'free' ? 'plan.resets' : 'plan.renews', {
          date: date(end, { month: 'short', day: 'numeric' }),
        })
      : null
  const soon = trial ? trial.left <= 3 : false
  const body = (
    <>
      <span className="pm-h">
        <span className="pm-name">{trial ? t('plan.proTrial') : t(PLAN_KEY[org.plan])}</span>
        {when ? <span className={soon ? 'pm-when soon' : 'pm-when'}>{when}</span> : null}
      </span>
      <span className="pm-n">
        {count}
        {aside ? (
          <span className={level ? `pm-aside ${level}` : 'pm-aside'} aria-hidden="true">
            {aside}
          </span>
        ) : null}
      </span>
      {share === null ? null : (
        <span className={level ? `meter-bar ${level}` : 'meter-bar'} aria-hidden="true">
          <i style={{ width: `${Math.max(2, share * 100)}%` }} />
        </span>
      )}
      {sessions?.capped ? (
        <span className="pm-capped" role="status">
          {t('usage.capped')}
        </span>
      ) : null}
    </>
  )
  return projectId ? (
    <Link
      className="nav-meter"
      to="/o/$orgId/p/$projectId/settings/$section"
      params={{ orgId: org.id, projectId, section: 'plan' }}
      title={trial ? t('trial.title') : t(PLAN_KEY[org.plan])}
    >
      {body}
    </Link>
  ) : (
    <div className="nav-meter">{body}</div>
  )
}
